import {invert} from 'lodash';

import {AsyncStatusesEnum} from '../../../../libs/shared/helpers/src/types/asyncStatuses';
import {wasAccountRecentlyCreated} from '../../../helpers/accountHelpers';
import {CancelToken} from '../../../helpers/asyncCancelHelpers';
import {delayAsync} from '../../../helpers/asyncHelpers';
import {deleteDomains, updateDomains} from '../../../helpers/models/accountAttributeUpdateHelpers';
import {ActionWithPayload, batch} from '../../../helpers/reduxHelpers';
import {AccountModel} from '../../../models/api/accountModel';
import {findPanelSequence} from '../../../services/panels';
import {buildPanelCancelToken, findPanelCancelToken} from '../../../services/panelsCancellers';
import {AppAction} from '../../appActions';
import {ThunkAction, ThunkAsyncAction} from '../../appThunk';
import {
  deleteAccountAsyncAction,
  fetchAccountAsyncAction,
  fetchAccountCardsAsyncAction,
  FetchAccountCardsOptions,
  fetchConversationsForAccountDomainAsyncAction,
  fetchConversationsForAccountIdAsyncAction,
  updateAccountAttributeAsyncAction,
} from '../../data/accounts/accountsActions';
import {getAccounts} from '../../data/dataSelectors';
import {showAccountsErrorBannerAction, showAccountsInfoBannerAction} from '../app/banners/bannersActions';
import {AccountsErrorBannerTypesEnum, AccountsInfoBannerTypesEnum} from '../app/banners/bannersState';
import {contactsManagerAccountDetailsClosePanelAction} from '../contacts/contactsManager/sidePanel/contactsManagerAccountDetailsActions';
import {removePanelAction, setPanelAction} from '../panelsActions';
import {getPanels, makeGetAppPanelIdFromPanel} from '../panelsSelectors';
import {panelIsAccountDetailsPanel, panelIsContactDetailsPanel} from '../panelsState';
import {
  AccountDetailsPanelId,
  ContactOrAccountDetailsPanelId,
  makeAccountDetailsPanelId,
  NewPanelId,
  PanelTypesEnum,
} from '../panelStateBase';
import {AccountDetailsPanelState} from './accountDetailsPanelState';

/*
 * Constants.
 */

const fetchAccountCardsOptions: FetchAccountCardsOptions = {limit: 6, offset: 0};

const accountCardsFetchDelay = 4000; // 4 seconds delay.
const accountConversationFetchDelay = 6000; // 6 seconds delay.

/*
 * Type enum.
 */

export enum AccountDetailsPanelActionsEnum {
  FETCH_ACCOUNT = '@@panels/account-details/FETCH_ACCOUNT',
  FETCH_ACCOUNT_COMPLETED = '@@panels/account-details/FETCH_ACCOUNT_COMPLETED',
  FETCH_ACCOUNT_FAILED = '@@panels/account-details/FETCH_ACCOUNT_FAILED',
  FETCH_ACCOUNT_CARDS = '@@panels/account-details/FETCH_ACCOUNT_CARDS',
  FETCH_ACCOUNT_CARDS_COMPLETED = '@@panels/account-details/FETCH_ACCOUNT_CARDS_COMPLETED',
  FETCH_ACCOUNT_CARDS_FAILED = '@@panels/account-details/FETCH_ACCOUNT_CARDS_FAILED',
  CLEAR_FETCHED_DATA = '@@panels/account-details/CLEAR_FETCHED_DATA',

  FETCH_ACCOUNT_CONVERSATIONS = '@@panels/account-details/FETCH_ACCOUNT_CONVERSATIONS',
  FETCH_ACCOUNT_CONVERSATIONS_COMPLETED = '@@panels/account-details/FETCH_ACCOUNT_CONVERSATIONS_COMPLETED',
  FETCH_ACCOUNT_CONVERSATIONS_FAILED = '@@panels/account-details/FETCH_ACCOUNT_CONVERSATIONS_FAILED',
}

/*
 * Types.
 */

interface AccountDetailsPanelGenericPayload {
  panelId: ContactOrAccountDetailsPanelId;
}

interface FetchAccountStartedActionPayload extends AccountDetailsPanelGenericPayload {
  accountId: number;
}

interface FetchAccountStartedAction extends ActionWithPayload<FetchAccountStartedActionPayload> {
  type: AccountDetailsPanelActionsEnum.FETCH_ACCOUNT;
}

interface FetchAccountCompletedActionPayload extends AccountDetailsPanelGenericPayload {
  accountId?: number;
}

interface FetchAccountCompletedAction extends ActionWithPayload<FetchAccountCompletedActionPayload> {
  type: AccountDetailsPanelActionsEnum.FETCH_ACCOUNT_COMPLETED;
}

interface FetchAccountFailedAction extends ActionWithPayload<AccountDetailsPanelGenericPayload> {
  type: AccountDetailsPanelActionsEnum.FETCH_ACCOUNT_FAILED;
}

interface FetchAccountCardsStartedAction extends ActionWithPayload<AccountDetailsPanelGenericPayload> {
  type: AccountDetailsPanelActionsEnum.FETCH_ACCOUNT_CARDS;
}

interface FetchAccountCardsCompletedActionPayload extends AccountDetailsPanelGenericPayload {
  cardIds: ReadonlyArray<number>;
}
interface FetchAccountCardsCompletedAction
  extends ActionWithPayload<FetchAccountCardsCompletedActionPayload> {
  type: AccountDetailsPanelActionsEnum.FETCH_ACCOUNT_CARDS_COMPLETED;
}

interface FetchAccountCardsFailedAction extends ActionWithPayload<AccountDetailsPanelGenericPayload> {
  type: AccountDetailsPanelActionsEnum.FETCH_ACCOUNT_CARDS_FAILED;
}

interface ClearFetchedDataAction extends ActionWithPayload<AccountDetailsPanelGenericPayload> {
  type: AccountDetailsPanelActionsEnum.CLEAR_FETCHED_DATA;
}

export interface FetchConversationsStartedAction
  extends ActionWithPayload<AccountDetailsPanelGenericPayload> {
  type: AccountDetailsPanelActionsEnum.FETCH_ACCOUNT_CONVERSATIONS;
}

interface FetchConversationsCompletedActionPayload extends AccountDetailsPanelGenericPayload {
  conversationIds: ReadonlyArray<number>;
  hasMoreConversations: boolean;
}

export interface FetchConversationsCompletedAction
  extends ActionWithPayload<FetchConversationsCompletedActionPayload> {
  type: AccountDetailsPanelActionsEnum.FETCH_ACCOUNT_CONVERSATIONS_COMPLETED;
}

export interface FetchConversationsFailedAction extends ActionWithPayload<AccountDetailsPanelGenericPayload> {
  type: AccountDetailsPanelActionsEnum.FETCH_ACCOUNT_CONVERSATIONS_FAILED;
}

/*
 * Action creators.
 */

function fetchAccountStartedAction(
  panelId: ContactOrAccountDetailsPanelId,
  accountId: number,
): FetchAccountStartedAction {
  return {
    type: AccountDetailsPanelActionsEnum.FETCH_ACCOUNT,
    payload: {
      panelId,
      accountId,
    },
  };
}

function fetchAccountCompletedAction(
  panelId: ContactOrAccountDetailsPanelId,
  accountId?: number,
): FetchAccountCompletedAction {
  return {
    type: AccountDetailsPanelActionsEnum.FETCH_ACCOUNT_COMPLETED,
    payload: {
      panelId,
      accountId,
    },
  };
}

function fetchAccountFailedAction(panelId: ContactOrAccountDetailsPanelId): FetchAccountFailedAction {
  return {
    type: AccountDetailsPanelActionsEnum.FETCH_ACCOUNT_FAILED,
    payload: {
      panelId,
    },
  };
}

function fetchAccountCardsStartedAction(
  panelId: ContactOrAccountDetailsPanelId,
): FetchAccountCardsStartedAction {
  return {
    type: AccountDetailsPanelActionsEnum.FETCH_ACCOUNT_CARDS,
    payload: {
      panelId,
    },
  };
}

function fetchAccountCardsCompletedAction(
  panelId: ContactOrAccountDetailsPanelId,
  cardIds: ReadonlyArray<number>,
): FetchAccountCardsCompletedAction {
  return {
    type: AccountDetailsPanelActionsEnum.FETCH_ACCOUNT_CARDS_COMPLETED,
    payload: {
      panelId,
      cardIds,
    },
  };
}

function fetchAccountCardsFailedAction(
  panelId: ContactOrAccountDetailsPanelId,
): FetchAccountCardsFailedAction {
  return {
    type: AccountDetailsPanelActionsEnum.FETCH_ACCOUNT_CARDS_FAILED,
    payload: {
      panelId,
    },
  };
}

function clearFetchedDataAction(panelId: ContactOrAccountDetailsPanelId): ClearFetchedDataAction {
  return {
    type: AccountDetailsPanelActionsEnum.CLEAR_FETCHED_DATA,
    payload: {
      panelId,
    },
  };
}

function fetchConversationsAction(panelId: ContactOrAccountDetailsPanelId): FetchConversationsStartedAction {
  return {
    type: AccountDetailsPanelActionsEnum.FETCH_ACCOUNT_CONVERSATIONS,
    payload: {
      panelId,
    },
  };
}

function fetchConversationsCompletedAction(
  panelId: ContactOrAccountDetailsPanelId,
  conversationIds: ReadonlyArray<number>,
  hasMoreConversations: boolean,
): FetchConversationsCompletedAction {
  return {
    type: AccountDetailsPanelActionsEnum.FETCH_ACCOUNT_CONVERSATIONS_COMPLETED,
    payload: {
      panelId,
      conversationIds,
      hasMoreConversations,
    },
  };
}

function fetchConversationsFailedAction(
  panelId: ContactOrAccountDetailsPanelId,
): FetchConversationsFailedAction {
  return {
    type: AccountDetailsPanelActionsEnum.FETCH_ACCOUNT_CONVERSATIONS_FAILED,
    payload: {
      panelId,
    },
  };
}

/*
 * Actions.
 */

export function popoutAccountDetailsAction(
  panelId: NewPanelId,
  accountId: number,
  popupWidth?: number,
): ThunkAction {
  return (dispatch, getState) => {
    const popupPanelId = makeAccountDetailsPanelId();
    const popupAppPanelId = makeGetAppPanelIdFromPanel()(getState(), {panelId});
    const [cancelTokenId] = buildPanelCancelToken(popupPanelId);

    const accounts = getAccounts(getState());
    const account = accounts[accountId];

    batch(() => {
      dispatch(
        setPanelAction({
          id: popupPanelId,
          type: PanelTypesEnum.ACCOUNT_DETAILS,
          status: account ? AsyncStatusesEnum.LOADED : AsyncStatusesEnum.LOADING,
          accountId,
          cancelTokenId,
          popupAppPanelId,
          sequence: findPanelSequence(),
          cardsState: undefined,
          popupWidth,
        }),
      );
    });
  };
}

export function navigateAccountDetailsAction(panelId: AccountDetailsPanelId, accountId: number): ThunkAction {
  return (dispatch, getState) => {
    const [cancelTokenId] = buildPanelCancelToken(panelId);

    const accounts = getAccounts(getState());
    const account = accounts[accountId];

    const panel: AccountDetailsPanelState = {
      id: panelId,
      type: PanelTypesEnum.ACCOUNT_DETAILS,
      status: account ? AsyncStatusesEnum.LOADED : AsyncStatusesEnum.LOADING,
      accountId,
      cancelTokenId,
      sequence: findPanelSequence(),
      cardsState: undefined,
    };
    dispatch(setPanelAction(panel));
  };
}

export function fetchAccountForAccountDetailsAction(
  panelId: ContactOrAccountDetailsPanelId,
  accountId: number,
): ThunkAction {
  return async (dispatch, getState) => {
    const panel = getPanels(getState())[panelId];
    if (!panel || (!panelIsAccountDetailsPanel(panel) && !panelIsContactDetailsPanel(panel))) {
      return;
    }
    const account = getAccounts(getState())[accountId];

    try {
      dispatch(fetchAccountStartedAction(panelId, accountId));

      if (!account) {
        await dispatch(fetchAccountAsyncAction(accountId));
      }

      dispatch(fetchAccountCompletedAction(panelId, accountId));
    } catch (error) {
      dispatch(fetchAccountFailedAction(panelId));
    }
  };
}

export function noAccountForAccountDetailsAction(panelId: ContactOrAccountDetailsPanelId): ThunkAction {
  return async (dispatch, getState) => {
    const panel = getPanels(getState())[panelId];
    if (!panel || (!panelIsAccountDetailsPanel(panel) && !panelIsContactDetailsPanel(panel))) {
      return;
    }
    dispatch(fetchAccountCompletedAction(panelId));
  };
}

export function fetchCardsForAccountDetailsAction(
  panelId: ContactOrAccountDetailsPanelId,
  accountId: number,
  isDelayed: boolean = false,
): ThunkAction {
  return async (dispatch, getState) => {
    const panel = getPanels(getState())[panelId];
    if (!panel || (!panelIsAccountDetailsPanel(panel) && !panelIsContactDetailsPanel(panel))) {
      return;
    }

    const account = getAccounts(getState())[accountId];

    try {
      dispatch(fetchAccountCardsStartedAction(panelId));
      // Naive + temporary approach because re-associating the contacts with an account after a
      // domain update can take some time. Long-term solution is to listen for a push event.
      if (isDelayed || (account && wasAccountRecentlyCreated(account))) {
        await delayAsync(accountCardsFetchDelay);
      }
      const {cardIds} = await dispatch(fetchAccountCardsAsyncAction(accountId, fetchAccountCardsOptions));

      dispatch(fetchAccountCardsCompletedAction(panelId, cardIds));
    } catch (error) {
      dispatch(fetchAccountCardsFailedAction(panelId));
    }
  };
}

export function domainsUpdatedAction(
  panelId: ContactOrAccountDetailsPanelId | undefined,
  account: AccountModel,
  updatedDomains: ReadonlyArray<string>,
): ThunkAsyncAction {
  return async (dispatch, getState) => {
    if (panelId) {
      const panel = getPanels(getState())[panelId];
      if (!panel || (!panelIsAccountDetailsPanel(panel) && !panelIsContactDetailsPanel(panel))) {
        return;
      }
    }

    try {
      const action = updateDomains(account.domains, updatedDomains);
      dispatch(updateAccountAttributeAsyncAction(account, action));

      if (!panelId) {
        return;
      }

      batch(() => {
        dispatch(fetchCardsForAccountDetailsAction(panelId, account.id, true));
        dispatch(showAccountsInfoBannerAction(panelId, AccountsInfoBannerTypesEnum.DOMAINS_UPDATED));
      });
    } catch (error) {
      if (!panelId) {
        return;
      }

      dispatch(showAccountsErrorBannerAction(panelId, AccountsErrorBannerTypesEnum.DOMAIN_UPDATE_FAILED));
    }
  };
}

export function domainsRemovedAction(
  panelId: ContactOrAccountDetailsPanelId,
  account: AccountModel,
): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const panel = getPanels(getState())[panelId];
    if (!panel || (!panelIsAccountDetailsPanel(panel) && !panelIsContactDetailsPanel(panel))) {
      return;
    }

    try {
      const action = deleteDomains(account.domains);
      dispatch(updateAccountAttributeAsyncAction(account, action));

      batch(() => {
        dispatch(fetchCardsForAccountDetailsAction(panelId, account.id, true));
        dispatch(showAccountsInfoBannerAction(panelId, AccountsInfoBannerTypesEnum.DOMAINS_REMOVED));
      });
    } catch (error) {
      dispatch(showAccountsErrorBannerAction(panelId, AccountsErrorBannerTypesEnum.DOMAIN_REMOVAL_FAILED));
    }
  };
}

export function closeAccountDetailsAction(
  panelId: ContactOrAccountDetailsPanelId,
  shouldNotRedirect: boolean = false,
): ThunkAction {
  return (dispatch, getState) => {
    const panel = getPanels(getState())[panelId];
    if (!panel || !panelIsAccountDetailsPanel(panel)) {
      return;
    }

    batch(() => {
      dispatch(removePanelAction(panelId));
      if (!panel.popupAppPanelId) {
        dispatch(contactsManagerAccountDetailsClosePanelAction(shouldNotRedirect));
      }
    });
  };
}

export function deleteAccountAndCloseAccountDetailsAction(
  panelId: AccountDetailsPanelId,
  accountId: number,
): ThunkAction {
  return (dispatch, getState) => {
    const panel = getPanels(getState())[panelId];
    if (!panel || !panelIsAccountDetailsPanel(panel)) {
      return;
    }

    try {
      batch(() => {
        dispatch(deleteAccountAsyncAction(accountId));
        dispatch(showAccountsInfoBannerAction(panelId, AccountsInfoBannerTypesEnum.ACCOUNT_DELETED));
        dispatch(removePanelAction(panelId));
        if (!panel.popupAppPanelId) {
          dispatch(contactsManagerAccountDetailsClosePanelAction());
        }
      });
    } catch (error) {
      dispatch(showAccountsErrorBannerAction(panelId, AccountsErrorBannerTypesEnum.DELETION_FAILED));
    }
  };
}

export function accountDetailsClearFetchedData(panelId: AccountDetailsPanelId): ThunkAction {
  return async (dispatch, getState) => {
    const panel = getPanels(getState())[panelId];
    if (!panel || (!panelIsAccountDetailsPanel(panel) && !panelIsContactDetailsPanel(panel))) {
      return;
    }

    dispatch(clearFetchedDataAction(panelId));
  };
}

export function fetchConversationsForAccountAsyncAction(
  panelId: ContactOrAccountDetailsPanelId,
  accountId?: number,
  domain?: string,
): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const panel = getPanels(getState())[panelId];
    if (!panel || (!panelIsAccountDetailsPanel(panel) && !panelIsContactDetailsPanel(panel))) {
      return;
    }

    const account = accountId ? getAccounts(getState())[accountId] : undefined;

    const cancelToken = findPanelCancelToken(panel.cancelTokenId);
    try {
      // Removes any previous conversation information that may be loaded.
      dispatch(fetchConversationsAction(panelId));

      // If the account was recently created, we need to wait for the backend to fetch some data before we can
      // ask for conversations.
      if (account && wasAccountRecentlyCreated(account)) {
        await delayAsync(accountConversationFetchDelay);
      }

      // Fetch the conversations.
      const {conversationIds, hasMoreConversations} = await dispatch(
        fetchRecentConversationsAsyncAction(cancelToken, accountId, domain),
      );

      // Update the store.
      dispatch(fetchConversationsCompletedAction(panelId, conversationIds, hasMoreConversations));
    } catch (error) {
      dispatch(fetchConversationsFailedAction(panelId));
    }
  };
}

function fetchRecentConversationsAsyncAction(
  cancelToken: CancelToken,
  accountId?: number,
  domain?: string,
): ThunkAsyncAction<{conversationIds: ReadonlyArray<number>; hasMoreConversations: boolean}> {
  return async (dispatch) => {
    if (accountId) {
      const conversations = await dispatch(
        fetchConversationsForAccountIdAsyncAction(accountId, {cancelToken}),
      );
      return {
        conversationIds: (conversations.conversations ?? []).map((conversation) => conversation.id),
        hasMoreConversations: conversations.hasMore,
      };
    }

    if (domain) {
      const conversations = await dispatch(
        fetchConversationsForAccountDomainAsyncAction(domain, {cancelToken}),
      );
      return {
        conversationIds: (conversations.conversations ?? []).map((conversation) => conversation.id),
        hasMoreConversations: conversations.hasMore,
      };
    }

    return {
      conversationIds: [],
      hasMoreConversations: false,
    };
  };
}

/*
 * Type guards.
 */

const accountDetailsPanelActionsEnumMap = invert(AccountDetailsPanelActionsEnum);
export function actionIsAccountDetailsPanelAction(action: AppAction): action is AccountDetailsPanelAction {
  return action.type in accountDetailsPanelActionsEnumMap;
}

/*
 * Union type.
 */

export type AccountDetailsPanelAction =
  | FetchAccountStartedAction
  | FetchAccountCompletedAction
  | FetchAccountFailedAction
  | FetchAccountCardsStartedAction
  | FetchAccountCardsCompletedAction
  | FetchAccountCardsFailedAction
  | ClearFetchedDataAction
  | FetchConversationsStartedAction
  | FetchConversationsCompletedAction
  | FetchConversationsFailedAction;
